import UserInfo from "../../../Form/UserInfo"; 

export type CaloriesResult = {
    overall?: number,
    maintain?: number,
    achieveHealthy?: number,
    bmiStatus?: string
    trimester1?: number,
    trimester2?: number,
    trimester3?: number,
    actualTrimester?: number 
}

type constantsType = {
    base: number,
    ageFactor: number,
    weightFactor: number,
    heightFactor: number,
    activityFactor: {
        low: number,
        mid: number,
        high: number
    }
    additional: number,
    round: string
}

function calculateCalories(userDetails: UserInfo){
    // Initialize
    let caloriesResult: CaloriesResult = {};
    

    let constants: constantsType = {
        base: 0.00,
        ageFactor: 0.00,
        weightFactor: 0.00,
        heightFactor: 0.00,
        activityFactor: { low: 0.00, mid: 0.00, high: 0.00 },
        additional: 0,
        round: ""
    }
    
    // Adjust pregnancy
    let pregnantAdditional = {
        1: 0,
        2: 340,
        3: 452
    }

    let breastfeedingAdditional = {
        "all": userDetails.breastfeedingTime === "0-6" ? 330 : 400,
        "half": userDetails.breastfeedingTime === "0-6" ? 70 : 200,
        "some": 0
    }

    // Determine constants
    if (userDetails.sex === 'male'){
        if (userDetails.age && userDetails.age <= 18){
            constants.base = 88.5
            constants.ageFactor = 61.9
            constants.weightFactor = 26.7
            constants.heightFactor = 903
            constants.activityFactor = { low: 1, mid: 1.13, high: 1.26 }
        } else {
            constants.base = 662
            constants.ageFactor = 9.53
            constants.weightFactor = 15.91
            constants.heightFactor = 539.6
            constants.activityFactor = { low: 1, mid: 1.12, high: 1.27 }
        }
    } else if (userDetails.sex === 'female'){
        if (userDetails.age && userDetails.age <= 18){
            constants.base = 135.3
            constants.ageFactor = 30.8
            constants.weightFactor = 10
            constants.heightFactor = 934
            constants.activityFactor = { low: 1, mid: 1.16, high: 1.31 }
            if (userDetails.pregnant && userDetails.pregnancyTrimester){
                constants.additional = pregnantAdditional[userDetails.pregnancyTrimester]; 
            } else if (userDetails.breastfeeding && userDetails.breastfeedingAmount) {
                constants.additional = breastfeedingAdditional[userDetails.breastfeedingAmount];
            }
        } else {
            constants.base = 354;
            constants.ageFactor = 6.91;
            constants.weightFactor = 9.36;
            constants.heightFactor = 726;
            constants.activityFactor = { low: 1, mid: 1.14, high: 1.27 }
            if (userDetails.pregnant && userDetails.pregnancyTrimester){
                constants.additional = pregnantAdditional[userDetails.pregnancyTrimester]; 
            } else if (userDetails.breastfeeding && userDetails.breastfeedingAmount) {
                constants.additional = breastfeedingAdditional[userDetails.breastfeedingAmount];
            }
        }
    }

    if (!userDetails.age || !userDetails.sex || !userDetails.weight || !userDetails.height) {
        return { overall: 2000 };
    }


    // Calculate BMI
    const bmi: number = calculateBMI(userDetails);
    caloriesResult.bmiStatus = determineBMIStatus(userDetails, bmi);
    const needsHealthyRecomendation: boolean = !userDetails.pregnant && !userDetails.breastfeeding && !isHealthyBMI(userDetails, bmi);

    if (userDetails.pregnant && userDetails.age && userDetails.age >= 14 && userDetails.age <= 50) {
        const trimester = userDetails.pregnancyTrimester || 1;
        
        // Use pregnancy-specific constants
        constants = {
            base: 387,
            ageFactor: 7.31,
            weightFactor: 10.9,
            heightFactor: 660.7,
            activityFactor: { low: 1, mid: 1.14, high: 1.27 },
            additional: 0,
            round: ""
        };

        // Calculate for each trimester
        caloriesResult.trimester1 = calculateForTrimester(userDetails, constants, 1);
        caloriesResult.trimester2 = calculateForTrimester(userDetails, constants, 2);
        caloriesResult.trimester3 = calculateForTrimester(userDetails, constants, 3);
        caloriesResult.actualTrimester = trimester;
        
        return caloriesResult;
    }

    // Calculate result
    if (userDetails.age && userDetails.activityLevel && userDetails.weight && userDetails.height){
        caloriesResult.maintain = calculateBaseCalories(userDetails, constants);
    }

    if (needsHealthyRecomendation) {
        const healthyWeight = calculateHealthyWeight(userDetails);
        caloriesResult.achieveHealthy = calculateBaseCalories(
            {...userDetails, weight: healthyWeight},
            constants
        );
    }

    return caloriesResult;
}

function calculateForTrimester(user: UserInfo, constants: any, trimester: 1 | 2 | 3): number {
    const triConstants: constantsType = {...constants};
    
    type TrimesterAdjustments = {
        [key: number]: number;
        1: number;
        2: number;
        3: number;
    };

    const adjustments: TrimesterAdjustments = {
        1: 0,
        2: 340,
        3: 452
    };
    
    triConstants.additional += adjustments[trimester];
    
    const bmi = calculateBMI(user);
    if (bmi >= 35) {
        const penalties = {
            1: 0,
            2: 180,
            3: 180
        };
        triConstants.additional -= penalties[trimester];
        triConstants.round = "down";
    }
    
    return calculateBaseCalories(user, triConstants);
}


function calculateBMI(user: UserInfo): number {
    if (!user.weight || !user.height) return 0;
    const heightInMeters = user.height / 100;
    return user.weight / (heightInMeters * heightInMeters);
}

function determineBMIStatus(user: UserInfo, bmi: number): string {
    if (user.age && user.age <= 18 && user.sex) {
        const percentiles = getAgePercentile(user.age, user.sex);
        if (bmi < percentiles["5th"]) return "underweight";
        if (bmi >= percentiles["95th"]) return "obese";
        if (bmi >= percentiles["85th"]) return "overweight";
        return "healthy";
    } else {
        if (bmi < 18.5) return "underweight";
        if (bmi >= 35) return "obese";
        if (bmi >= 25) return "overweight";
        return "healthy";
    }
}

function isHealthyBMI(user: UserInfo, bmi: number): boolean {
    const status = determineBMIStatus(user, bmi);
    return status === "healthy";
}

function calculateHealthyWeight(user: UserInfo): number {
    if (user.sex && user.height){
        if (user.age && user.age <= 18) {
            const percentiles = getAgePercentile(user.age, user.sex);
            return percentiles.median * Math.pow(user.height/100, 2);
        }
        const idealBMI = user.sex === "male" ? 22.5 : 21.5;
        return idealBMI * Math.pow(user.height/100, 2);
    }
    return 0;
}

function calculateBaseCalories(user: UserInfo, constants: any): number {
    let calories = user.age && user.activityLevel && user.weight && user.height &&
                constants.base - (constants.ageFactor * user.age) + 
                (constants.activityFactor[user.activityLevel] * 
                ((constants.weightFactor * user.weight) + 
                (constants.heightFactor * (user.height/100)))) +
                constants.additional;
    
    calories = 200 * Math.round(calories / 200);
    return Math.min(calories, 3200);
}

function getAgePercentile(age: number, sex: string): any {
    if (sex === "Male") {
        switch (age) {
        case 9:
            return {
                "5th": 14.07936677,
                median: 16.39605916,
                "85th": 19.00335954,
                "95th": 21.61938426,
            };
        case 10:
            return {
                "5th": 14.37920031,
                median: 16.91512487,
                "85th": 19.78938034,
                "95th": 22.68718928,
            };
        case 11:
            return {
                "5th": 14.75988661,
                median: 17.50138161,
                "85th": 20.60910189,
                "95th": 23.72897155,
            };
        case 12:
            return {
                "5th": 15.2111263,
                median: 18.1387275,
                "85th": 21.43852405,
                "95th": 24.71352325,
            };
        case 13:
            return {
                "5th": 15.71971118,
                median: 18.81201584,
                "85th": 22.25973057,
                "95th": 25.62255542,
            };
        case 14:
            return {
                "5th": 16.26979755,
                median: 19.50672885,
                "85th": 23.06061552,
                "95th": 26.45091448,
            };
        case 15:
            return {
                "5th": 16.84323264,
                median: 20.20858236,
                "85th": 23.83459013,
                "95th": 27.20682538,
            };
        case 16:
            return {
                "5th": 17.41995236,
                median: 20.90294449,
                "85th": 24.58032971,
                "95th": 27.91225209,
            };
        case 17:
            return {
                "5th": 17.97833773,
                median: 21.57417053,
                "85th": 25.30172632,
                "95th": 28.60329919,
            };
        case 18:
            return {
                "5th": 18.49520062,
                median: 22.20541249,
                "85th": 26.00822631,
                "95th": 29.33030127,
            };
        default:
            return {
                "5th": 18.5,
                median: 22.5,
                "85th": 27,
                "95th": 30,
            }
        }
    } else {
        switch (age) {
        case 9:
            return {
                "5th": 13.87942647,
                median: 16.57626713,
                "85th": 19.54662051,
                "95th": 22.39788709,
            };
        case 10:
            return {
                "5th": 14.21040706,
                median: 17.16096656,
                "85th": 20.42657139,
                "95th": 23.56546313,
            };
        case 11:
            return {
                "5th": 14.61099007,
                median: 17.78355575,
                "85th": 21.30973548,
                "95th": 24.70603086,
            };
        case 12:
            return {
                "5th": 15.06558978,
                median: 18.42002284,
                "85th": 22.16531999,
                "95th": 25.78730634,
            };
        case 13:
            return {
                "5th": 15.55572457,
                median: 19.04811118,
                "85th": 22.96953922,
                "95th": 26.78861781,
            };
        case 14:
            return {
                "5th": 16.05992392,
                median: 19.64746266,
                "85th": 23.70570337,
                "95th": 27.70078892,
            };
        case 15:
            return {
                "5th": 16.55358366,
                median: 20.19980767,
                "85th": 24.3643699,
                "95th": 28.52601678,
            };
        case 16:
            return {
                "5th": 17.00880162,
                median: 20.6891237,
                "85th": 24.94355805,
                "95th": 29.27780966,
            };
        case 17:
            return {
                "5th": 17.39427286,
                median: 21.10163241,
                "85th": 25.44895346,
                "95th": 29.98109225,
            };
        case 18:
            return {
                "5th": 17.67537444,
                median: 21.42548351,
                "85th": 25.89392241,
                "95th": 30.67259539,
            };
        default:
            return {
                "5th": 18.5,
                median: 21.5,
                "85th": 22,
                "95th": 30,
            }
        }
    }
}

export default calculateCalories;